import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Constants, ERRORS } from '../../models/constants';
import { CoreCommonService } from '../../providers/services/common.service';
import { CoreHttpService } from '../../utilities/services/http.service';
import { throwError } from 'rxjs';
import { SubSink } from 'subsink';
// import { environment } from '@environments/environment';
import { HttpParams } from '@angular/common/http';
import { distinctUntilChanged } from 'rxjs/operators';
import { Inject } from '@angular/core';
import { CoreUtilityService } from '../../utilities/services/utility.service';

@Component({
  selector: 'app-searchable-dropdown',
  templateUrl: './searchable-dropdown.component.html'
})
export class SearchableDropdownComponent implements OnChanges {
  @Input() field;
  @Input() form;
  @Input() type;
  @Input() parentScope: any;
  @Input() dynamicControlScope: any;
  @Input() isBundle: boolean;
  subdomain: any;
  options = [];
  optionscopy = [];
  @Output() controlReady = new EventEmitter<boolean>();
  private eventSubscriptionsList: SubSink;

  constructor(private coreCommonService: CoreCommonService,
    private coreHttpService: CoreHttpService, @Inject('ENV_CONFIG') public environment: any ,
    private coreUtilityService: CoreUtilityService) {
    this.subdomain = this.environment.applicationType;
  }

  /**
   * A callback method that is invoked if any data-bound properties change
   */
  ngOnChanges() {
    this.eventSubscriptionsList = new SubSink();
    this.handleControlLoad();
  }

  /**
   * This method will handle control load
   * NOTE: Sequence of function calls need to be exactly same as below
   */
  private async handleControlLoad() {
    await this.registerEvents();
    await this.loadDropdownOptionsList(this.form, this.field);
    this.notifyControlLoadComplete();
  }

  /**
   * This method will register the events
   */
  private async registerEvents() {
    try {
      const valueChange = this.registerDropdownValueChange(this.field, this.form);
      const eventEmitter = this.registerChangeEmitterEvent(this.field, this.form);
      await Promise.all([valueChange, eventEmitter]);
      return true;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * getter function to check mandatory fields
   */
  get isMandatory(): boolean {
    try {
      return this.field.validations.mandatory.value;
    } catch (error) {
      return false;
    }
  }

  /**
   * This method will notify when control is ready
   */
  private notifyControlLoadComplete() {
    this.controlReady.emit(true);
  }

  /**
   * This method will register dropdown value changes
   */
  private registerChangeEmitterEvent(field: object, form: FormGroup) {
    try {
      if (!field.hasOwnProperty('onChangeEvent') || field['onChangeEvent'].length === 0) {
        return;
      }

      for (const eventSchema of field['onChangeEvent']) {
        this.eventSubscriptionsList.sink = form
          .get(field['logicalName'])
          .valueChanges
          .subscribe((value) => {
            this.callFunctionByName(eventSchema.functionName, field, value, form, eventSchema.params);
          });
      }
      return true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * This method will register change emitter event
   */
  private registerDropdownValueChange(field: object, form: FormGroup) {
    try {
      if (
        Constants.DROPDOWNS_LIST.indexOf(field['fieldType']) === -1 ||
        !field.hasOwnProperty('changeEmitterFields') ||
        field['changeEmitterFields'].length === 0
      ) {
        return;
      }

      const changeEmitterFields = field['changeEmitterFields'] || [];
      for (const changeEmitterField of changeEmitterFields) {
        // INFO: Do not add distinctUntilChanged filter since it will block populate chained dependent dropdown's
        //  E.g. Visa Type on Eligibility Criteria page
        this.eventSubscriptionsList.sink = form
          .get(changeEmitterField['targetFieldLogicalName'])
          .valueChanges.pipe(distinctUntilChanged())
          .subscribe((value) => {
            this.callFunctionByName(changeEmitterField.functionName, field, value, form, changeEmitterField.params);
          });
      }
      return true;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * options array
   * @param logicalName - logical name
   */
  optionsArray(logicalName: string) {
    const data = this.options[logicalName];
    return Array.isArray(data) ? data : [];
  }

  /**
   * This method will call the schema functions by name
   */
  async callFunctionByName(fnName: string, ...params: any) {
    try {
      let scope: any;
      if (this[fnName]) {
        scope = this;
      } else if (this.dynamicControlScope[fnName]) {
        scope = this.dynamicControlScope;
      } else if (this.parentScope[fnName]) {
        scope = this.parentScope;
      } else {
        throw new Error(ERRORS.FUNCTION_NOT_FOUND);
      }
      params ? scope[fnName](...params) : scope[fnName]();
    } catch (error) {
      throwError(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
    }
    return true;
  }

  /**
   * This method will load dropdown options list
   */
  private async loadDropdownOptionsList(form: FormGroup, field: any) {
    try {
      const formField = form.get(field.logicalName);
      const apiObj = this.coreCommonService.extractApiAsPerDomain(field['optionsListApi']);
      // TODO: Load options for dropdown if it doesnt depend on any other fields ie. changeEmitterFields
      if (!field.hasOwnProperty('changeEmitterFields')) {
        let data: any;
        if (field['defaultValueOptionSet'] && field['defaultValueOptionSet'].length !== 0) {
          data = field['defaultValueOptionSet'];
        } else {
          if (apiObj['params'] && Array.isArray(apiObj['params']) && apiObj['params'].length > 0) {
            const params = new HttpParams({ fromObject: this.coreCommonService.extractAndConstructParams(apiObj['params']) });
            data = await this.coreHttpService
              .doGet(apiObj['url'], params)
              .toPromise()
              .then((val) => val)
              .catch((error) => {
                throw error;
              });
          } else {
            data = await this.coreHttpService
              .doGet(apiObj['url'])
              .toPromise()
              .then((val) => val)
              .catch((error) => {
                throw error;
              });
          }
        }
        this.options = data || [];
        this.optionscopy = data || [];
        const valueKey = field['optionsListApi'][this.subdomain].valueKey;
        const value = formField.value
          ? formField.value
          : this.coreUtilityService.strictCompare(data.length, 1)
          ? data[0][valueKey]
          : await this.coreCommonService.fetchDefaultValue(field.defaultValueLookup, form);
        formField.setValue(value || null);
      }
      formField.updateValueAndValidity();
      return true;
    } catch (error) {
      console.log(`${ERRORS.FORM_DATA_LOAD_FAILED}: ${error}`);
    }
  }

  // TODO: Remove this function and reuse loadDropdownOptionsList instead. Probably will have to add last if to later fn.
  /**
   * This method will listen for any update in dropdown field
   */
  private async updateListenerDropdownField(targetField: object, selectedValue: any, form: FormGroup, params?: any[]) {
    try {
      // INFO: Number of params in 'optionsListApi' should be same and in same order as number of values in
      // 'changeEmitterFields.params' array in JSON
      let data: any;

      if (targetField['defaultValueOptionSet'] && targetField['defaultValueOptionSet'].length !== 0) {
        data = targetField['defaultValueOptionSet'];
      } else {
        const api = this.coreCommonService.extractApiAsPerDomain(targetField['optionsListApi']);
        const constructedParam = this.coreCommonService.extractAndConstructParams(api?.params, form);

        data = await this.coreHttpService
          .doCheck(api['url'], api?.type, constructedParam)
          .toPromise()
          .then((val) => val)
          .catch((err) => throwError(err));
      }
      this.options = data || [];
      if (targetField['displayInfo'] && targetField['displayInfo']['visible']) {
        let tooltip = [...new Set(data.map((obj) => obj.toolTip))];
        // @ts-ignore
        tooltip = tooltip.toString().replace(/[,]/g, '\n\n');
        targetField['displayInfo']['message'] = tooltip;
      }
      this.optionscopy = data || [];

      if (targetField['logicalName'] === this.field['logicalName']) {
        const valueKey = this.field.optionsListApi[this.subdomain].valueKey;
        const existOption = this.optionscopy.filter((val) => val[valueKey] === this.form.get(targetField['logicalName']).value);
        if (existOption.length > 0) {
          this.form.get(targetField['logicalName']).setValue(this.form.get(targetField['logicalName']).value);
        } else {
          const value = this.coreUtilityService.strictCompare(data.length, 1) ? data[0][valueKey] : null;
          this.form.get(targetField['logicalName']).setValue(value);
        }
      }
      this.form.get(targetField['logicalName']).updateValueAndValidity();
    } catch (error) {
      console.log(ERRORS.ERROR_OCCURED, ' : ', error);
    }
    return true;
  }

  // -------------------------------------------- Additional code for searchable code ------------------------------------------
  // TODO: Optmize this function if possible
  /**
   * This method will change the dropdown values
   */
  changeDrpodownValues(dropDownPayload: any, logicalName: string) {
    try {
      const displayKey = this.field.optionsListApi[this.subdomain].displayKey;
      if (dropDownPayload.target.value) {
        this.options = this.optionscopy.filter((val) => val[displayKey].toLowerCase().includes(dropDownPayload.target.value.toLowerCase()));
      } else if (!dropDownPayload.target.value && this.form.get(logicalName).value) {
        this.options = this.optionscopy;
      } else {
        this.options = this.optionscopy;
      }
    } catch (error) {
      console.log(error);
    }
  }

  // TODO: Check this function
  /**
   * This method onchange select valut
   */
  onValueChanged(event: any, logicalName: string) {
    if (event && event.target && event.target.value) {
      const displayKey = this.field.optionsListApi[this.subdomain].displayKey;
      const valueKey = this.field.optionsListApi[this.subdomain].valueKey;
      const existOption = this.optionscopy.filter((val) => val[displayKey].toLowerCase() === event.target.value.toLowerCase());
      if (existOption.length > 0) {
        this.form.get(logicalName).setValue(existOption[0][valueKey]);
      } else {
        this.form.get(logicalName).setValue(null);
      }
    }
  }

  // TODO: Check this function
  /**
   * This method will set the list of options
   */
  setOptionList(isToggled, logicalName) {
    if (isToggled) {
      this.options = this.optionscopy;
      document.querySelector('#' + logicalName + '>div>input')['value'] = '';
    }
  }

  /**
   * The trackBy function takes the index and the current item as arguments
   * and needs to return the unique identifier for this item
   * @param index - item index
   */
  trackByFn(index: number, item): number {
    if (!item) {
      return null;
    }
    return index;
  }
}
